import { useEffect, useRef, useState, type CSSProperties, type PointerEvent } from "react";
import { Check, RefreshCw } from "lucide-react";
import { loadHumanGateChallenge } from "@/api";
import type { HumanGateChallenge } from "@/types";
import { cn } from "@/lib/utils";

type HumanGateProps = {
  name?: string;
  onVerifiedChange?: (verified: boolean) => void;
};

const knobSize = 34;

function clamp(value: number, min: number, max: number) {
  return Math.min(Math.max(value, min), max);
}

export function HumanGate({ name = "human_gate", onVerifiedChange }: HumanGateProps) {
  const trackRef = useRef<HTMLDivElement | null>(null);
  const pointerIdRef = useRef<number | null>(null);
  const startedAtRef = useRef(0);
  const movesRef = useRef(0);

  const [challenge, setChallenge] = useState<HumanGateChallenge | null>(null);
  const [position, setPosition] = useState(0);
  const [verifiedPayload, setVerifiedPayload] = useState("");
  const [status, setStatus] = useState<"loading" | "ready" | "hold" | "verified" | "error">("loading");

  const verified = status === "verified";

  const reset = () => {
    onVerifiedChange?.(false);
    setVerifiedPayload("");
    setPosition(0);
    setStatus("loading");
    loadHumanGateChallenge()
      .then((payload) => {
        setChallenge(payload);
        setStatus("ready");
      })
      .catch(() => {
        setChallenge(null);
        setStatus("error");
      });
  };

  useEffect(() => {
    reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function maxTravel() {
    const width = trackRef.current?.getBoundingClientRect().width ?? 0;
    return Math.max(1, width - knobSize);
  }

  function unitsFromPosition(nextPosition: number) {
    if (!challenge) return 0;
    return Math.round((nextPosition / maxTravel()) * challenge.trackUnits);
  }

  function positionFromUnits(units: number) {
    if (!challenge) return 0;
    return clamp((units / challenge.trackUnits) * maxTravel(), 0, maxTravel());
  }

  function positionFromPointer(clientX: number) {
    const rect = trackRef.current?.getBoundingClientRect();
    if (!rect) return 0;
    return clamp(clientX - rect.left - knobSize / 2, 0, maxTravel());
  }

  function completeIfMatched(nextPosition: number) {
    if (!challenge) return;
    const durationMs = Math.round(performance.now() - startedAtRef.current);
    const releaseX = unitsFromPosition(nextPosition);
    if (releaseX < challenge.target - challenge.tolerance) {
      setStatus("ready");
      return;
    }
    if (durationMs < challenge.minDurationMs) {
      setStatus("hold");
      return;
    }
    const x = challenge.target;
    setPosition(positionFromUnits(x));
    const payload = JSON.stringify({
      nonce: challenge.nonce,
      target: challenge.target,
      trackUnits: challenge.trackUnits,
      tolerance: challenge.tolerance,
      issuedAt: challenge.issuedAt,
      expiresAt: challenge.expiresAt,
      minDurationMs: challenge.minDurationMs,
      signature: challenge.signature,
      x,
      durationMs,
      moves: movesRef.current,
    });
    setVerifiedPayload(payload);
    setStatus("verified");
    onVerifiedChange?.(true);
  }

  function handlePointerDown(event: PointerEvent<HTMLDivElement>) {
    if (!challenge || verified) return;
    pointerIdRef.current = event.pointerId;
    startedAtRef.current = performance.now();
    movesRef.current = 0;
    event.currentTarget.setPointerCapture(event.pointerId);
    const next = positionFromPointer(event.clientX);
    setPosition(next);
    setStatus("ready");
  }

  function handlePointerMove(event: PointerEvent<HTMLDivElement>) {
    if (pointerIdRef.current !== event.pointerId || !challenge || verified) return;
    movesRef.current += 1;
    setPosition(positionFromPointer(event.clientX));
  }

  function handlePointerUp(event: PointerEvent<HTMLDivElement>) {
    if (pointerIdRef.current !== event.pointerId || !challenge || verified) return;
    pointerIdRef.current = null;
    const next = positionFromPointer(event.clientX);
    setPosition(next);
    completeIfMatched(next);
  }

  const targetLeft = challenge ? `${(challenge.target / challenge.trackUnits) * 100}%` : "50%";
  const knobLeft = `${position}px`;
  const fillWidth = `calc(${knobLeft} + ${knobSize / 2}px)`;

  return (
    <div className="mt-3 grid gap-1.5">
      <input type="hidden" name={name} value={verifiedPayload} />
      <div className="flex items-center justify-between gap-3">
        <span className="font-mono text-[10px] font-semibold uppercase tracking-wider opacity-75">
          HumanGate / local
        </span>
        <button
          type="button"
          onClick={reset}
          aria-label="Reset HumanGate"
          className="inline-flex size-7 items-center justify-center rounded-md border border-[var(--portal-border)] bg-[var(--portal-control)] text-[var(--portal-text)] transition-colors hover:border-[var(--portal-text)]/35"
        >
          <RefreshCw className="size-3.5" strokeWidth={1.7} />
        </button>
      </div>
      <div
        ref={trackRef}
        role="slider"
        aria-valuemin={0}
        aria-valuemax={challenge?.trackUnits ?? 100}
        aria-valuenow={challenge ? unitsFromPosition(position) : 0}
        aria-disabled={!challenge || verified}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerCancel={() => {
          pointerIdRef.current = null;
          if (!verified) setStatus("ready");
        }}
        className={cn(
          "relative h-10 touch-none select-none overflow-hidden rounded-md border",
          "border-[var(--portal-border)] bg-[var(--portal-control)]",
          status === "error" && "border-[var(--danger)]/45",
          verified && "border-[var(--positive)]/45",
        )}
        style={{
          "--human-gate-target": targetLeft,
          "--human-gate-fill": fillWidth,
        } as CSSProperties}
      >
        <div className="absolute inset-y-0 left-0 w-[var(--human-gate-fill)] bg-[var(--portal-text)]/[0.07]" />
        <div className="absolute inset-y-1 left-[var(--human-gate-target)] w-px bg-[var(--portal-text)]/40" />
        <div className="absolute inset-0 flex items-center justify-center font-mono text-[10px] font-semibold uppercase tracking-wider text-[var(--portal-muted)]">
          {verified ? "" : status === "loading" ? "SYNC" : status === "hold" ? "HOLD" : status === "error" ? "RELOAD" : "VERIFY"}
        </div>
        <div
          className={cn(
            "absolute top-1/2 flex size-[34px] -translate-y-1/2 items-center justify-center rounded-md border",
            "border-[var(--portal-border)] bg-[var(--portal-card)] shadow-[0_8px_22px_-18px_rgba(28,25,23,0.7)]",
            verified && "border-[var(--positive)]/55 text-[var(--positive)]",
          )}
          style={{ left: knobLeft }}
        >
          {verified ? <Check className="size-4" strokeWidth={1.8} /> : <span className="h-3 w-3 border-r border-t border-current opacity-70 rotate-45" />}
        </div>
      </div>
    </div>
  );
}
